import { Button } from '@talentmesh/ui/components/button';
import { cn } from '@talentmesh/ui/lib/utils';
import React, { useMemo, useState } from 'react';
import { ProfileExperienceResponse } from '../../../../api/DTO/ProfileExperienceResponse';
import AiSearchCompanyDetail from '../../../AiSearchCompanyDetail/AiSearchCompanyDetail';
import CompanyLogo from '../../../CompanyLogo/CompanyLogo';
import SimpleTextFormatter from '../SimpleTextFormatter';
import { calculateDuration } from './formatDuration';
import UIStrings from '@/Utils/UIStrings';

interface ProfileExperienceProps {
    experience: ProfileExperienceResponse;
}

const ProfileExperience = ({ experience }: ProfileExperienceProps) => {
    const {
        title,
        company,
        startDate,
        endDate,
        location,
        description,
        isCurrent,
        startDateMonth,
        startDateYear,
        durationInMonths,
        companyDomain,
        companyUrl,
    } = experience;
    const [anchorEl, setAnchorEl] = useState<HTMLElement>();
    const open = !!anchorEl;
    const durationString = useMemo(
        () => calculateDuration(isCurrent, durationInMonths, startDateMonth, startDateYear),
        [isCurrent, durationInMonths, startDateMonth, startDateYear],
    );

    const openCompanyDetail = (e: React.MouseEvent<HTMLElement>) => companyDomain && setAnchorEl(e.currentTarget);
    const closeCompanyDetail = () => setAnchorEl(undefined);

    return (
        <div className="flex flex-row gap-3">
            <CompanyLogo
                companyName={company}
                companyDomain={companyDomain}
                size="40px"
                className={cn(companyDomain && 'cursor-pointer')}
                onClick={openCompanyDetail}
            />
            <div>
                <h4 className="text-lg font-semibold tracking-tight">{title}</h4>
                <Button
                    size="sm"
                    variant="link"
                    className="text-foreground h-fit w-fit p-0"
                    onClick={openCompanyDetail}
                >
                    {company}
                </Button>
                <p className="text-sm">
                    {startDate} {isCurrent ? `- ${UIStrings.Current}` : endDate && `- ${endDate}`} {durationString}
                </p>
                <p className="text-muted-foreground text-sm">{location}</p>
                {description && (
                    <div className="mt-2">
                        <SimpleTextFormatter value={description} />
                    </div>
                )}
            </div>
            {company && (
                <AiSearchCompanyDetail
                    open={open}
                    companyName={company}
                    companyDomain={companyDomain}
                    companyLinkedInUrl={companyUrl}
                    onClose={closeCompanyDetail}
                    anchorEl={anchorEl}
                />
            )}
        </div>
    );
};

export default ProfileExperience;
